import React, { useEffect, useRef, useState } from "react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import "./Map.css";
import { geojson } from "./data";

mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;
const bounds = [
  [34.2654333839, 29.5013261988],
  [35.8363969256, 33.2774264593]
];
const fitBoundsOptions = { padding: 20, duration: 0 };

const Map = () => {
  const [map, setMap] = useState(null); // eslint-disable-line no-unused-vars
  const containerRef = useRef(null);

  useEffect(() => {
    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: "mapbox://styles/amnestydecoders/ckprmfxve00he17o8a7kwki0c",
      bounds,
      fitBoundsOptions,
      scrollZoom: false
    });
    const navigation = new mapboxgl.NavigationControl({
      showCompass: false
    });
    const handleResize = () => {
      map.setMinZoom(null);
      map.fitBounds(bounds, fitBoundsOptions);
      map.setMinZoom(map.getZoom());
      map.removeControl(navigation);
      map.addControl(navigation);
    };

    map.on("load", () => {
      map.addControl(navigation);
      map.addSource("events", {
        type: "geojson",
        data: geojson,
        cluster: true,
        clusterMaxZoom: 14,
        clusterRadius: 50
      });

      map.addLayer({
        id: "clusters",
        type: "circle",
        source: "events",
        filter: ["has", "point_count"],
        paint: {
          "circle-color": "#ffff00",
          "circle-radius": ["step", ["get", "point_count"], 20, 10, 30, 20, 40],
          "circle-opacity": 0.8,
          "circle-stroke-width": 1,
          "circle-stroke-color": "#ffff00"
        }
      });

      map.addLayer({
        id: "cluster-count",
        type: "symbol",
        source: "events",
        filter: ["has", "point_count"],
        layout: {
          "text-field": "{point_count_abbreviated}",
          "text-font": [
            "Amnesty Trade Gothic Bold No- 2",
            "Arial Unicode MS Bold"
          ],
          "text-size": 12
        }
      });

      map.addLayer({
        id: "unclustered-point",
        type: "circle",
        source: "events",
        filter: ["!", ["has", "point_count"]],
        paint: {
          "circle-color": "#ffff00",
          "circle-radius": 6,
          "circle-opacity": 1,
          "circle-stroke-width": 1,
          "circle-stroke-color": "#ffffff"
        }
      });

      // Inspect a cluster on click
      map.on("click", "clusters", e => {
        const { properties, geometry } = map.queryRenderedFeatures(e.point, {
          layers: ["clusters"]
        })[0];
        const { cluster_id: clusterId } = properties;

        map
          .getSource("events")
          .getClusterExpansionZoom(clusterId, (err, zoom) => {
            if (err) return;

            map.easeTo({
              center: geometry.coordinates,
              zoom
            });
          });
      });

      map.on("click", "unclustered-point", e => {
        const { geometry, properties } = e.features[0];
        const coordinates = geometry.coordinates.slice();
        const {
          graphic,
          event_date: eventDate,
          latitude,
          longitude,
          events_count: eventsCount,
          source_links: sourceLinks,
          content_descriptions: contentDescriptions,
          categories
        } = properties;

        // Ensure that if the map is zoomed out such that multiple copies of the
        // feature are visible, the popup appears over the copy being pointed
        // to.
        while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
          coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
        }

        new mapboxgl.Popup()
          .setLngLat(coordinates)
          .setHTML(
            `${
              graphic
                ? `<span class="graphic">WARNING: GRAPHIC CONTENT</span><br>`
                : ""
            }
              <strong>Number of events:</strong> ${eventsCount}<br>
              <strong>Date:</strong> ${eventDate}<br>
              ${categories
                .split(";")
                .map(
                  (c, i, a) =>
                    `<strong>Event type${
                      a.length > 1 ? ` ${i + 1}` : ""
                    }:</strong> ${c}<br>`
                )
                .join("")}
              ${contentDescriptions
                .split(";")
                .map(
                  (c, i, a) =>
                    `<strong>Event description${
                      a.length > 1 ? ` ${i + 1}` : ""
                    }:</strong> ${c}<br>`
                )
                .join("")}
                <strong>Longitude:</strong> ${longitude.toFixed(
                  4
                )}<br><strong>Latitude:</strong> ${latitude.toFixed(4)}<br>
                ${sourceLinks
                  .split(";")
                  .map(
                    (s, i, a) =>
                      `<strong>Source${
                        a.length > 1 ? ` ${i + 1}` : ""
                      }:</strong> ${
                        s.startsWith("https://oneamnesty")
                          ? `<a href="${s}" target="_blank" rel="noreferrer">Amnesty SharePoint</a>`
                          : `<a href="${s}" target="_blank" rel="noreferrer">${s}</a>`
                      }<br>`
                  )
                  .join("")}`
          )
          .addTo(map);
      });

      map.on("mouseenter", "clusters", () => {
        map.getCanvas().style.cursor = "pointer";
      });

      map.on("mouseleave", "clusters", () => {
        map.getCanvas().style.cursor = "";
      });

      map.on("mouseenter", "unclustered-point", () => {
        map.getCanvas().style.cursor = "pointer";
      });

      map.on("mouseleave", "unclustered-point", () => {
        map.getCanvas().style.cursor = "";
      });

      setMap(map);

      handleResize();

      window.addEventListener("resize", handleResize);
    });

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return <div className="map__container" ref={containerRef} />;
};

export default Map;
